import { Teleporter } from '../../../shared/Teleporter';
import { resolveTeleporterVisualType } from '../../../shared/teleporterPresentation';
import { Position } from '../../../shared/CharacterTypes';
import { gameLogger } from '../../../utils/GameLogger';
// import { TeleporterEntity, createTeleporter } from '../../entities/Teleporter';
import ConfigManager from '../../../config/boot/ConfigManager';
import { createGraphics } from '../../rendering/utils/Graphics';
import { Z_LAYERS } from '../../rendering/Z_LAYERS';
import { isTeleporterDebugEnabled } from '../../../shared/url/params';
import { isPortalSealed, portalUnsealQuest } from '../../../shared/logic/portalSeal';
import { portalRequiredQuest } from '../../../shared/logic/portalQuestGate';
import { isNpcOnlyFerryPortal } from '../../../shared/npc/ferryRoutes';

/**
 * Manages teleporter lifecycle and operations
 * Handles loading, clearing, creating, and querying teleporters
 */
export class TeleporterManager {
  private static instance: TeleporterManager;
  private teleporters: Teleporter[] = [];
  private currentLevelId: string | null = null;
  private teleporterEntities: any[] = []; // Changed to any[] for server compatibility

  private constructor() { }

  public static getInstance(): TeleporterManager {
    if (!TeleporterManager.instance) {
      TeleporterManager.instance = new TeleporterManager();
    }
    if (typeof window !== 'undefined') {
      (window as any).__teleporterManager = TeleporterManager.instance;
    }
    return TeleporterManager.instance;
  }

  /**
   * Load teleporter configurations for a specific level
   */
  public loadTeleporters(levelId: string, levelData?: import('../../../shared/CharacterTypes').PlatformerLevel): void {
    // Clear existing teleporters first
    this.clearTeleporters();
    this.currentLevelId = levelId;

    // 1. Legacy Config (Removed)
    const configTeleporters: Teleporter[] = [];

    // 2. Load from Level Data (platforms property) - NEW
    let levelTeleporters: Teleporter[] = [];
    if (levelData && levelData.platforms) {
      levelTeleporters = levelData.platforms
        .filter(p => p.type === 'teleporter' && !isNpcOnlyFerryPortal(levelId, p.id))
        .map(p => {
          // Parse target from explicit nested config or fallback to legacy/flat props
          const pAny = p as any;
          const cfg = p.teleporterConfig || {};
          // Read editor link from entity.properties (where editor saves it) or flat
          const editorLink = pAny.properties?.teleporterLink || pAny.teleporterLink;

          return new Teleporter({
            id: p.id,
            // Map JSON stores platform x/y as the TOP-LEFT corner; the entity
            // anchors at the pad's bottom-center so the hologram column + labels
            // stack upward. (This transform is why placement looks "off" if you
            // assume a center origin.)
            x: p.x + ((p.width || 64) / 2),      // top-left → horizontal center
            y: p.y + (p.height || 96),           // top-left → bottom edge
            width: p.width || 64,
            height: p.height || 128,
            // Priority: Nested Config -> Editor Link (properties) -> Flat Property -> Default
            targetMapId: cfg.targetMapId || editorLink?.targetMapId || pAny.targetMapId || levelId,
            targetSpawnId: editorLink?.targetId || cfg.targetSpawnId || pAny.targetSpawnId || (pAny.attributes?.type === 'exp_shrine' ? '' : (p.id + '_dest')),
            targetName: cfg.targetName || pAny.targetName,
            targetSubheader: cfg.targetSubheader || cfg.subheading || pAny.targetSubheader || pAny.subheading,
            direction: cfg.direction || pAny.direction,
            targetX: pAny.targetX, // Keep legacy or add to config if needed
            targetY: pAny.targetY,
            isDefaultSpawn: !!pAny.isDefaultSpawn, // Deprecated?
            requiresActivation: cfg.requiresActivation ?? (pAny.requiresActivation !== false), // Default true unless explicitly false
            activationType: cfg.activationType || ((cfg.requiresActivation === false || pAny.requiresActivation === false) ? 'auto' : 'manual'),
            visualType: resolveTeleporterVisualType(cfg.visualType ?? pAny.visualType),
            visualEffect: cfg.visualEffect || pAny.visualEffect,
            snapToGround: pAny.snapToGround,
            targetSnapToGround: pAny.targetSnapToGround,
            // Data-driven level gate: nested config wins, then flat property on
            // the level-JSON teleporter entity. Unset (undefined) means the gate
            // inherits the destination zone's recommended level band.
            minLevel: cfg.minLevel ?? editorLink?.minLevel ?? pAny.minLevel,
            // Showcase ungate flag (Biome Nexus tour pads): nested config wins,
            // else flat property on the level-JSON teleporter platform.
            bypassZoneGate: cfg.bypassZoneGate ?? pAny.bypassZoneGate,
            // Three-tier gate knobs (on-ramp design §3): level-data authored
            // when present; unset → portalGate.ts destination policy/defaults.
            hardGate: cfg.hardGate ?? pAny.hardGate,
            softWarnBand: cfg.softWarnBand ?? pAny.softWarnBand,
            sealed: isPortalSealed(pAny),
            concealed: cfg.concealed ?? pAny.concealed,
            contextualAction: cfg.contextualAction ?? pAny.contextualAction,
            contextualPrompt: (cfg as any).contextualPrompt ?? pAny.contextualPrompt,
            accessibleName: cfg.accessibleName ?? pAny.accessibleName,
            lightCoreAnchor: (cfg as any).lightCoreAnchor ?? pAny.lightCoreAnchor,
            contextualRadius: (cfg as any).contextualRadius ?? pAny.contextualRadius,
            winkAnchors: (cfg as any).winkAnchors ?? pAny.winkAnchors,
            requiredQuest: portalRequiredQuest(pAny) ?? undefined, // quest gate (lane BL) → prompt (lane BO)
            unsealQuest: portalUnsealQuest(pAny) ?? undefined, // sealed portal's opener (portalSeal.ts)
            attributes: pAny.attributes // Pass generic attributes (e.g. for shrines)
          });
        });
    }

    this.teleporters = [...configTeleporters.map(c => new Teleporter(c)), ...levelTeleporters];

    gameLogger.info('map', `[TeleporterManager] Loaded ${this.teleporters.length} teleporters for ${levelId}`);
    this.teleporters.forEach(t => {
      gameLogger.info('map', `  - Teleporter ${t.id}: x=${t.x}, y=${t.y}, target=${t.targetMapId}, visual=${t.visualType}`);
    });
  }

  /**
   * Clear all teleporters and their entities
   */
  public clearTeleporters(): void {
    // Clear teleporter entities from scene
    this.teleporterEntities.forEach(entity => {
      if (entity) {
        entity.destroy();
      }
    });
    this.teleporterEntities = [];
    this.teleporters = [];
    this.currentLevelId = null;

    gameLogger.info('map', '[TeleporterManager] Teleporters cleared');
  }

  /**
   * Create teleporter entities in the scene
   */
  public async createTeleportersInScene(scene: any): Promise<void> {
    if (!scene) return;

    const { createTeleporter } = await import('../../entities/Teleporter' as any);

    // Don't clear teleporters array here, just the entities
    this.teleporterEntities.forEach(e => e.destroy());
    this.teleporterEntities = [];

    const showTeleporterDebug = isTeleporterDebugEnabled()
      || ConfigManager.getInstance().gameConstants.debug?.showTeleporters === true;

    this.teleporters.forEach(teleporterData => {
      // Find the target teleporter to get its descriptive name
      const targetTele = teleporterData.targetMapId === this.currentLevelId
        ? this.teleporters.find(t => t.id === teleporterData.targetSpawnId)
        : undefined;
      const targetName = teleporterData.targetName?.trim() || targetTele?.name;

      const teleporter = createTeleporter(scene, {
        id: teleporterData.id,
        x: teleporterData.x,
        y: teleporterData.y,
        targetMapId: teleporterData.targetMapId,
        targetSpawnId: teleporterData.targetSpawnId,
        targetName: targetName,
        targetSubheader: teleporterData.targetSubheader,
        direction: teleporterData.direction,
        targetX: teleporterData.targetX,
        targetY: teleporterData.targetY,
        // The authored floor reaches the label ("Lv. N+"); without it the
        // label fell back to the zone band (lane BG: Pig Beach's level-6
        // Dark Forest door read "Lv. 3+").
        minLevel: teleporterData.minLevel,
        bypassZoneGate: teleporterData.bypassZoneGate,
        hardGate: teleporterData.hardGate,
        softWarnBand: teleporterData.softWarnBand,
        sealed: teleporterData.sealed,
        concealed: teleporterData.concealed,
        contextualAction: teleporterData.contextualAction,
        contextualPrompt: teleporterData.contextualPrompt,
        accessibleName: teleporterData.accessibleName,
        lightCoreAnchor: teleporterData.lightCoreAnchor,
        contextualRadius: teleporterData.contextualRadius,
        winkAnchors: teleporterData.winkAnchors,
        requiredQuest: teleporterData.requiredQuest,
        unsealQuest: teleporterData.unsealQuest,
        isDefaultSpawn: teleporterData.isDefaultSpawn,
        requiresActivation: teleporterData.requiresActivation,
        activationType: teleporterData.activationType,
        visualType: teleporterData.visualType,
        visualEffect: teleporterData.visualEffect,
        snapToGround: teleporterData.snapToGround,
        targetSnapToGround: teleporterData.targetSnapToGround,
        width: teleporterData.width || 64,
        height: teleporterData.height || 128,
        showDebugLabels: showTeleporterDebug,
      });
      this.teleporterEntities.push(teleporter);
    });

    // Resolve smart links after creation
    this.resolveSmartLinks();

    // Debug visuals if enabled
    if (showTeleporterDebug) {
      this.drawDebugConnections(scene);
    }

    gameLogger.info('map', `[TeleporterManager] Created ${this.teleporterEntities.length} teleporter entities`);
  }

  /**
   * Resolves smart links between teleporters in the current list
   * Pairs teleporters with the same linkId
   */
  private resolveSmartLinks(): void {
    const linkMap = new Map<string, Teleporter[]>();

    // Group by linkId
    this.teleporters.forEach(t => {
      if (t.linkId) {
        if (!linkMap.has(t.linkId)) {
          linkMap.set(t.linkId, []);
        }
        linkMap.get(t.linkId)?.push(t);
      }
    });

    // Pair them up
    linkMap.forEach((group, linkId) => {
      if (group.length === 2) {
        const [t1, t2] = group;
        // Link t1 -> t2
        t1.targetX = t2.x;
        t1.targetY = t2.y;
        // If they are on the same map, we can set ID, otherwise we assume same map for now
        // OR if different maps, we need targetMapId.
        // Current limitation: Smart Linking primarily for intra-map or if targetMapId is pre-set.
        // If targetMapId is NOT set, assume same map.
        if (!t1.targetMapId) t1.targetMapId = t2.targetMapId || 'current'; // 'current' logic handled by MapSystem check

        // Link t2 -> t1
        t2.targetX = t1.x;
        t2.targetY = t1.y;
        if (!t2.targetMapId) t2.targetMapId = t1.targetMapId || 'current';

        gameLogger.info('map', `[TeleporterManager] Smart Linked '${linkId}': ${t1.id} <-> ${t2.id}`);
      } else if (group.length > 0) {
        gameLogger.warn('map', `[TeleporterManager] Smart Link '${linkId}' has ${group.length} teleporters (expected 2). Skipping auto-link.`);
      }
    });
  }

  /**
   * Draws debug lines showing teleporter connections.
   * Substrate-aware via IGraphics — gated by gameConstants.debug.showTeleporters.
   */
  public drawDebugConnections(scene: any): void {
    const graphics = createGraphics(scene, { depth: Z_LAYERS.TELEPORTER_DEBUG_CONNECTIONS });

    this.teleporters.forEach(t => {
      // 1. Draw box at origin
      graphics.strokeRect(t.x - 32, t.y - 128, 64, 128, 0x00FF00, 2, 1);

      // 2. Draw line to target
      if (t.targetX !== undefined && t.targetY !== undefined) {
        if ((!t.targetMapId || t.targetMapId === 'current' || t.targetMapId === ConfigManager.getInstance().gameConstants?.debug?.currentMapId)) {
          // Connection line (light cyan)
          graphics.strokeLine(t.x, t.y - 64, t.targetX, t.targetY - 64, 0x00FFFF, 2, 0.5);

          // Arrow head — two strokes off the target end at +/- 30deg
          const angle = Math.atan2((t.targetY - 64) - (t.y - 64), t.targetX - t.x);
          const arrowLength = 20;
          const ax1 = t.targetX - arrowLength * Math.cos(angle - Math.PI / 6);
          const ay1 = (t.targetY - 64) - arrowLength * Math.sin(angle - Math.PI / 6);
          const ax2 = t.targetX - arrowLength * Math.cos(angle + Math.PI / 6);
          const ay2 = (t.targetY - 64) - arrowLength * Math.sin(angle + Math.PI / 6);
          graphics.strokeLine(t.targetX, t.targetY - 64, ax1, ay1, 0x00FFFF, 2, 1);
          graphics.strokeLine(t.targetX, t.targetY - 64, ax2, ay2, 0x00FFFF, 2, 1);
        } else {
          // External Map Link — short magenta stub upward
          graphics.strokeLine(t.x, t.y - 64, t.x, t.y - 100, 0xFF00FF, 2, 0.8);
        }
      }
    });
  }

  /**
   * Get teleporter at a specific position
   */
  public getTeleporterAt(x: number, y: number): Teleporter | null {
    // Check if player is standing on any teleporter
    for (let i = 0; i < this.teleporterEntities.length; i++) {
      const entity = this.teleporterEntities[i];
      // We pass a dummy object with x, y that matches character expected by checkOverlap
      if (entity && entity.active && entity.checkOverlap({ x, y })) {
        return this.teleporters[i];
      }
    }
    return null;
  }

  /**
   * Get default spawn position from teleporters
   */
  public getDefaultSpawnPosition(): Position | null {
    const defaultTeleporter = this.teleporters.find(t => t.isDefaultSpawn);
    if (defaultTeleporter) {
      return { x: defaultTeleporter.x, y: defaultTeleporter.y };
    }
    return null;
  }

  /**
   * Get all teleporters
   */
  public getTeleporters(): Teleporter[] {
    return this.teleporters;
  }

  /**
   * Get all teleporter entities
   */
  public getTeleporterEntities(): any[] {
    return this.teleporterEntities;
  }

  /**
   * Add a new teleporter
   */
  public addTeleporter(teleporter: Teleporter): void {
    this.teleporters.push(teleporter);
    gameLogger.info('map', `[TeleporterManager] Added teleporter: ${teleporter.id}`);
  }

  /**
   * Remove a teleporter by ID
   */
  public removeTeleporter(teleporterId: string): boolean {
    const index = this.teleporters.findIndex(t => t.id === teleporterId);
    if (index !== -1) {
      this.teleporters.splice(index, 1);

      // Also remove the entity if it exists
      if (this.teleporterEntities[index]) {
        const entity = this.teleporterEntities[index];
        if (entity && !entity.active) {
          entity.destroy();
        }
        this.teleporterEntities.splice(index, 1);
      }

      gameLogger.info('map', `[TeleporterManager] Removed teleporter: ${teleporterId}`);
      return true;
    }
    return false;
  }

  /**
   * Update teleporter (for real-time changes)
   */
  public updateTeleporter(teleporterId: string, updates: Partial<Teleporter>): boolean {
    const teleporter = this.teleporters.find(t => t.id === teleporterId);
    if (teleporter) {
      Object.assign(teleporter, updates);
      gameLogger.info('map', `[TeleporterManager] Updated teleporter: ${teleporterId}`);
      return true;
    }
    return false;
  }
}
